import React, { useState } from 'react';
import { useSubscriptions } from '@/modules/subscriptions/api';
import { STRIPE_PREMIUM_PRICE_ID } from '@/modules/subscriptions/stripePrices';
import { Modal, Button, CurrencySelector } from '@/modules/ui/components';
import { detectCurrency } from '@/modules/subscriptions/utils/currency';
import * as Sentry from '@sentry/browser';

const PRICING = {
  USD: { symbol: '$', amount: '8', code: 'USD' },
  EUR: { symbol: '€', amount: '7', code: 'EUR' },
  GBP: { symbol: '£', amount: '6', code: 'GBP' }
};

function PremiumUpgradePrompt({ 
  isOpen, 
  onClose, 
  title = "Upgrade to Premium",
  message = "You've reached the limit of your current plan. Upgrade to Premium for unlimited access to all features.",
  feature = "premium feature"
}) {
  const { createCheckoutSession } = useSubscriptions();
  const [selectedCurrency, setSelectedCurrency] = useState(detectCurrency);
  const [isUpgrading, setIsUpgrading] = useState(false);

  const handleUpgrade = async () => {
    try {
      setIsUpgrading(true);
      console.log('Creating checkout session for upgrade:', { feature, selectedCurrency });
      
      // Use the single price ID with the selected currency  
      const response = await createCheckoutSession(STRIPE_PREMIUM_PRICE_ID, selectedCurrency);
      
      if (response.url) {
        window.location.href = response.url;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      Sentry.captureException(error, {
        extra: { 
          action: 'handleUpgrade',
          feature,
          selectedCurrency,
          context: 'PremiumUpgradePrompt'
        }
      });
    } finally {
      setIsUpgrading(false);
    }
  };

  const selectedPricing = PRICING[selectedCurrency];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="space-y-6">
        <div className="text-center">
          <div className="text-6xl mb-4">⭐</div>
          <p className="text-gray-700 dark:text-gray-300 text-lg">
            {message}
          </p>
        </div>

        {/* Pricing and Currency Selection */}
        <div className="bg-brand-50 dark:bg-brand-900/20 p-4 rounded-xl border border-brand-100 dark:border-brand-900/40">
          <div className="text-center mb-4">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-4">
              Choose Your Currency
            </h4>

            {/* Currency Selector */}
            <div className="mb-4">
              <CurrencySelector
                currencies={Object.keys(PRICING)}
                value={selectedCurrency}
                onChange={setSelectedCurrency}
                disabled={isUpgrading}
              />
            </div>

            {/* Selected Pricing Display */}
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                {selectedPricing.symbol}{selectedPricing.amount}
                <span className="text-lg font-normal text-gray-600 dark:text-gray-400">/month</span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Billed monthly in {selectedPricing.code}
              </p>
            </div>
          </div>

          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
            Premium Benefits:
          </h4>
          <ul className="text-gray-700 dark:text-gray-300 space-y-1">
            <li>• Unlimited squads</li>
            <li>• Unlimited players per squad</li>
            <li>• Save match history permanently</li>
            <li>• Advanced game statistics</li>
            <li>• Priority email support</li>
          </ul>
        </div>

        <div className="space-y-3">
          <Button
            onClick={handleUpgrade}
            disabled={isUpgrading}
            loading={isUpgrading}
            size="large"
            fullWidth
            className="cursor-pointer"
          >
            Upgrade to Premium
          </Button>

          <div className="text-center">
            <Button
              variant="link"
              size="small"
              onClick={onClose}
              disabled={isUpgrading}
            >
              Continue with Free Plan
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default PremiumUpgradePrompt;